export interface MediaItem {
  type: "image" | "video" | "youtube";
  src: string;
  caption?: string;
  poster?: string;
}

export interface TechItem {
  name: string;
  category: "frontend" | "backend" | "tooling" | "design";
}

export interface Project {
  id: number;
  slug: string;
  title: string;
  description: string;
  overview: string;
  role: string;
  duration: string;
  teamSize?: number;
  image?: string;
  video?: string;
  poster?: string;
  media: MediaItem[];
  tags: string[];
  techStack: TechItem[];
  responsibilities: string[];
  live?: string;
  github?: string;
  status: "active" | "archived" | "classified" | "on-hold" | "going-live";
}

export const projects: Project[] = [
  {
    id: 7,
    slug: "source-web",
    title: "Source Web",
    description:
      "Internal UI component library powering Vodafone markets worldwide.",
    overview:
      "Source Web is a shared UI library used internally across all Vodafone markets globally. Built as a TurboRepo monorepo where every component ships as an independent package — developed, tested, and deployed in isolation. Collaborated with multiple Vodafone group teams to gather feedback and align the library with market-specific needs. All components are documented in Storybook for developer onboarding.",
    role: "Frontend Engineer",
    duration: "Ongoing",
    status: "classified",
    image: "/projects/source-web-1.png",
    media: [
      {
        type: "image",
        src: "/projects/source-web-1.png",
        caption: "Source Web — component library overview",
      },
    ],
    tags: [
      "TypeScript",
      "React",
      "Storybook",
      "TurboRepo",
      "Tailwind",
      "pnpm",
    ],
    techStack: [
      { name: "TypeScript", category: "frontend" },
      { name: "React", category: "frontend" },
      { name: "Tailwind", category: "frontend" },
      { name: "Storybook", category: "tooling" },
      { name: "TurboRepo", category: "tooling" },
      { name: "pnpm", category: "tooling" },
      { name: "Babel", category: "tooling" },
      { name: "ESLint", category: "tooling" },
      { name: "Prettier", category: "tooling" },
      { name: "Docusaurus", category: "tooling" },
    ],
    responsibilities: [
      "Designed and built reusable UI components using compound component pattern, shipped as independent packages in a TurboRepo monorepo.",
      "Collaborated with teams from multiple Vodafone markets globally to gather feedback and adapt components to their specific needs.",
      "Documented every component in Storybook, enabling developers across markets to integrate them with minimal friction.",
      "Enforced type-safe styling with tailwind and consistent code quality through ESLint and Prettier.",
      "Managed monorepo dependency graph and build pipelines with TurboRepo and pnpm workspaces.",
    ],
  },
  {
    id: 2,
    slug: "vodafone-albania",
    title: "Vodafone Albania — Web Platform",
    description:
      "Next.js SSR platform powering vodafone.al — ~300 pages of dynamic content driven by Contentful CMS, rendered through a three-layer data pipeline from CMS fetch to component display.",
    overview:
      "vodafone.al is Vodafone Albania's main consumer-facing website, serving hundreds of pages of product, promotional, and support content entirely managed by CMS authors. Built on Next.js with server-side rendering, each page resolves its content through a three-layer pipeline: the first layer fetches data from Contentful based on the incoming URL path; the second layer maps and validates the CMS payload against the shape each UI component expects; the third layer assembles the mapped data and renders the components in the exact order defined by the CMS. This architecture lets non-technical content authors build and rearrange pages without touching code, while the frontend remains type-safe and predictable.",
    role: "Frontend Engineer",
    duration: "Finish",
    image: "/projects/vodafone-web-2.png",
    video: "/projects/vodafone-web.mp4",
    poster: "/projects/vodafone-web-poster.webp",
    media: [
      {
        type: "video",
        src: "/projects/vodafone-web.mp4",
        caption: "Vodafone Albania homepage — page navigation",
        poster: "/projects/vodafone-web-poster.webp",
      },
      {
        type: "image",
        src: "/projects/vodafone-web-2.png",
        caption: "Vodafone Albania — CMS-driven content rendering",
      },
      {
        type: "image",
        src: "/projects/vodafone-web-3.png",
        caption: "Vodafone Albania — Page structure",
      },
    ],
    tags: ["Next.js", "TypeScript", "Contentful", "SSR"],
    techStack: [
      { name: "Next.js", category: "frontend" },
      { name: "TypeScript", category: "frontend" },
      { name: "Tailwind", category: "frontend" },
      { name: "Zod", category: "frontend" },
      { name: "Contentful CMS", category: "backend" },
      { name: "Nx", category: "tooling" },
    ],
    responsibilities: [
      "Built the first layer of the CMS pipeline: fetching structured content from Contentful based on the requested page path, with SSR support for instant page loads",
      "Developed the mapping and validation layer that translates raw Contentful payloads into typed component props, ensuring data integrity across all page types",
      "Implemented the render layer that assembles validated component data and outputs pages in the exact component order defined by CMS authors",
      "Maintained and extended the component library used across all ~300 pages, keeping UI consistent while supporting full content flexibility",
    ],
    live: "https://www.vodafone.al",
    status: "active",
  },
  {
    id: 1,
    slug: "vodafone-esim",
    title: "Vodafone eSIM",
    description:
      "End-to-end eSIM onboarding platform for tourists — plan selection, online KYC, payment, eSign, and instant QR code delivery for device activation.",
    overview:
      "Vodafone Albania's eSIM platform lets tourists purchase and activate a local eSIM entirely online — no store visit required. The flow takes the user from plan selection through authentication, online KYC identity verification, payment processing, and electronic signature, then delivers a full purchase summary and a scannable QR code to install the eSIM directly on their device. Built as a microfrontend within Vodafone's broader app ecosystem, the project required tight integration with identity, payment, and telecom provisioning APIs while keeping the UX frictionless for international visitors.",
    role: "Frontend Engineer",
    duration: "Ongoing",
    image: "/projects/esim-5.png",
    media: [
      {
        type: "image",
        src: "/projects/esim-5.png",
        caption: "eSIM plan selection",
      },
      {
        type: "image",
        src: "/projects/esim-2.png",
        caption: "User authentication",
      },
      {
        type: "image",
        src: "/projects/esim-3.png",
        caption: "Online KYC verification",
      },
      {
        type: "image",
        src: "/projects/esim-4.png",
        caption: "Payment & eSign",
      },
      {
        type: "image",
        src: "/projects/esim-1.png",
        caption: "eSIM summary & QR code",
      },
    ],
    tags: ["React", "TypeScript", "Microfrontend"],
    techStack: [
      { name: "Next.js", category: "frontend" },
      { name: "TypeScript", category: "frontend" },
      { name: "Zustand", category: "frontend" },
      { name: "Zod", category: "frontend" },
      { name: "Nx", category: "tooling" },
    ],
    responsibilities: [
      "Built the full multi-step onboarding flow: eSIM plan selection, user authentication, online KYC identity verification, payment, and eSign",
      "Implemented the purchase summary screen and QR code delivery step, giving users everything needed to install the eSIM directly on their device",
      "Integrated with Vodafone's identity and telecom provisioning APIs to handle KYC validation and eSIM profile generation in real time",
      "Developed the payment and electronic signature steps with strict form validation using Zod, ensuring data integrity across the full flow",
      "Delivered the feature as a microfrontend module within Vodafone's Nx monorepo, keeping it independently deployable and consistent with the shared UI component library",
    ],
    live: "https://home.vodafone.al/esim",
    status: "active",
  },
  {
    id: 5,
    slug: "g2mint",
    title: "G2Mint — Miles TMS",
    description:
      "Next-generation Transportation Management System powering real-time freight operations, multi-carrier rate management, and AI-driven workflow automation across 40+ integrations.",
    overview:
      "G2Mint's Miles platform is a freight TMS built for brokers, shippers, and carriers. Miles Command ships a full TMS with pre-configured dashboards and plugins; Miles Boost is an API/EDI-first rate engine that plugs into any existing system. I built core frontend features across both products — data-dense shipment tracking dashboards, the multi-carrier rate overlay UI, and workflow configuration screens — all handling live logistics data at scale.",
    role: "Frontend Engineer",
    duration: "3 years",
    teamSize: 13,
    image: "/projects/g2mint-1-poster.webp",
    video: "/projects/g2mint-1.mp4",
    poster: "/projects/g2mint-1-poster.webp",
    media: [
      {
        type: "video",
        src: "/projects/g2mint-2.mp4",
        poster: "/projects/g2mint-2-poster.webp",
        caption: "Miles Boost — live UI interaction",
      },
      {
        type: "youtube",
        src: "https://www.youtube.com/embed/ijDNZW2aOR0",
        caption: "Miles — Product Introduction",
      },
      {
        type: "youtube",
        src: "https://www.youtube.com/embed/OdgT7eZKLNg",
        caption: "Miles AI Workflow Builder",
      },
      {
        type: "youtube",
        src: "https://www.youtube.com/embed/YIgH4vqYPmw",
        caption: "Rate Overlay Feature Demo",
      },
      {
        type: "youtube",
        src: "https://www.youtube.com/embed/Z2lOKowDORQ",
        caption: "Miles Card View UI",
      },
    ],
    tags: ["React", "TypeScript", "MUI", "Tanstack Query", "NX"],
    techStack: [
      { name: "React", category: "frontend" },
      { name: "TypeScript", category: "frontend" },
      { name: "MUI", category: "frontend" },
      { name: "React Hook Form", category: "frontend" },
      { name: "Yup", category: "frontend" },
      { name: "Tanstack Query", category: "frontend" },
      { name: "Nx", category: "tooling" },
      { name: "Vite", category: "tooling" },
      { name: "Playwright", category: "tooling" },
      { name: "Cypress", category: "tooling" },
    ],
    responsibilities: [
      "Built data-dense shipment tracking dashboards handling real-time logistics data across 40+ carrier and partner integrations",
      "Developed the multi-carrier rate overlay UI — surfacing blended contract, broker, and spot rates in a single decision interface",
      "Implemented complex form flows for shipment creation, carrier assignment, and automated freight settlement using React Hook Form and Yup",
      "Contributed to the modular frontend architecture using Nx, enabling both Miles Command and Miles Boost to share core UI components",
      "Maintained high code quality through active code reviews and consistent use of TypeScript across the entire frontend surface",
    ],
    live: "https://g2mint.com",
    status: "active",
  },
  {
    id: 6,
    slug: "pulsepad",
    title: "PulsePad",
    description:
      "Healthtech web app for healthcare professionals. Audio recording, AI-powered transcription, structured consultation reports, and Stripe subscription management.",
    overview:
      "PulsePad is a healthtech platform built for healthcare professionals to record patient consultations and receive AI-generated structured reports. The audio pipeline runs RecordRTC capture through FFmpeg WASM encoding before upload, with SignalR WebSockets streaming real-time transcription status back to the client. The app covers the full product lifecycle: Auth0-powered onboarding, TipTap rich-text report editing, a library of saved recordings, and a Stripe-integrated subscription flow with monthly/yearly plans, checkout sessions, and customer portal access.",
    role: "Frontend Engineer",
    duration: "Ongoing",
    image: "/projects/pulsepad.png",
    video: "/projects/pulsepad.mp4",
    poster: "/projects/pulsepad-poster.webp",
    media: [
      {
        type: "video",
        src: "/projects/pulsepad.mp4",
        poster: "/projects/pulsepad-poster.webp",
        caption: "Recording dashboard",
      },
      {
        type: "image",
        src: "/projects/pulsepad.png",
        caption: "Subscription Plan",
      },
      {
        type: "image",
        src: "/projects/pulsepad-2.png",
        caption: "Upload Recording",
      },
      {
        type: "image",
        src: "/projects/pulsepad-3.png",
        caption: "Welcome screen",
      },
    ],
    tags: ["React", "TypeScript", "Auth0", "Stripe", "SignalR", "FFmpeg"],
    techStack: [
      { name: "React 18", category: "frontend" },
      { name: "TypeScript", category: "frontend" },
      { name: "Tailwind CSS", category: "frontend" },
      { name: "Radix UI", category: "frontend" },
      { name: "Redux Toolkit", category: "frontend" },
      { name: "React Hook Form", category: "frontend" },
      { name: "TipTap", category: "frontend" },
      { name: "WaveSurfer.js", category: "frontend" },
      { name: "Auth0", category: "backend" },
      { name: "Stripe", category: "backend" },
      { name: "SignalR", category: "backend" },
      { name: "Axios", category: "backend" },
      { name: "FFmpeg WASM", category: "tooling" },
      { name: "RecordRTC", category: "tooling" },
      { name: "i18next", category: "tooling" },
    ],
    responsibilities: [
      "Built the full audio pipeline: RecordRTC capture → pause/resume controls → FFmpeg WASM encoding → upload to backend",
      "Integrated Microsoft SignalR for real-time transcription status updates streamed directly into the UI",
      "Implemented TipTap-based rich text editor for reviewing and editing AI-generated consultation reports",
      "Wired up Stripe checkout sessions, plan switching, and customer portal for monthly/yearly subscription management",
      "Integrated Auth0 for authentication and built the onboarding flow for new healthcare professional accounts",
      "Added multi-language support with i18next, persisted via localStorage",
    ],
    status: "on-hold",
  },
  {
    id: 8,
    slug: "sea-world-med",
    title: "Sea World Med",
    description:
      "Maritime medical compliance platform for ship operators: vessels, fleets, flag-state medical scales, medical stock and consumption, behind secure 2FA onboarding. In final QA and going live.",
    overview:
      "Sea World Med is a marine medical intelligence platform that helps maritime medical providers keep every vessel's medical chest compliant with its flag state's regulations. Shipboard captains and shoreside clinicians record stock, consumption and monthly usage, while admins manage customers, fleets, vessels, regulatory scales, the SKU catalogue, supplier packaging imports and units of measure. I built the React frontend end to end on top of a .NET REST API: a strict three-layer architecture (components → containers → pages) enforced by lint rules, Zod validation on every API response, around 30 feature modules, and CI/CD to Azure. The hardest part was not the code but the product side. The client's domain knowledge lived in spreadsheets, regulations and conversations, so I ran sessions with them to pull out the real data rules (flag-state medical scales, unit conversions, packaging imports, user roles), turned their ideas into user stories and acceptance criteria, and agreed every API contract with the backend team before building it.",
    role: "Frontend Engineer",
    duration: "Ongoing",
    status: "going-live",
    image: "/projects/sea-world-med-dashboard.webp",
    media: [
      {
        type: "image",
        src: "/projects/sea-world-med-login.webp",
        caption: "Sign in",
      },
      {
        type: "image",
        src: "/projects/sea-world-med-confirm-email.webp",
        caption: "Email-link onboarding — email confirmed",
      },
      {
        type: "image",
        src: "/projects/sea-world-med-set-password.webp",
        caption: "Choose password with strength policy",
      },
      {
        type: "image",
        src: "/projects/sea-world-med-2fa-method.webp",
        caption: "Two-factor setup — authenticator app or email code",
      },
      {
        type: "image",
        src: "/projects/sea-world-med-2fa-setup.webp",
        caption: "Authenticator setup — QR code, manual key, trusted browser",
      },
      {
        type: "image",
        src: "/projects/sea-world-med-dashboard.webp",
        caption: "Dashboard — fleet compliance KPIs, vessel map and attention list",
      },
      {
        type: "image",
        src: "/projects/sea-world-med-vessels.webp",
        caption: "Vessels — profiles, customer/flag and medical scale",
      },
      {
        type: "image",
        src: "/projects/sea-world-med-admin.webp",
        caption: "Administration — users, roles & permissions, audit history",
      },
    ],
    tags: ["React", "TypeScript", "Tailwind", "TanStack Query", "Azure"],
    techStack: [
      { name: "React 19", category: "frontend" },
      { name: "TypeScript", category: "frontend" },
      { name: "Tailwind CSS v4", category: "frontend" },
      { name: "TanStack Query", category: "frontend" },
      { name: "Zustand", category: "frontend" },
      { name: "React Hook Form", category: "frontend" },
      { name: "Zod", category: "frontend" },
      { name: "React Router", category: "frontend" },
      { name: "Mapbox GL", category: "frontend" },
      { name: ".NET REST API", category: "backend" },
      { name: "Axios", category: "backend" },
      { name: "Azure Web App", category: "backend" },
      { name: "Vite", category: "tooling" },
      { name: "Vitest", category: "tooling" },
      { name: "Testing Library", category: "tooling" },
      { name: "oxlint", category: "tooling" },
      { name: "Husky", category: "tooling" },
      { name: "GitHub Actions", category: "tooling" },
    ],
    responsibilities: [
      "Built the frontend end to end as a React 19 SPA over a .NET REST API, with a three-layer architecture (components → containers → pages) whose boundaries are enforced by oxlint import rules",
      "Validated every API response with Zod at the API boundary and normalised backend errors in Axios interceptors, so contract changes fail loudly instead of silently breaking screens",
      "Delivered the full auth suite: email-link onboarding, password policy with strength meter, two-factor authentication by authenticator app (QR / manual key) or email code, trusted browsers and token refresh",
      "Built the data-dense operations modules — customers, fleets, vessels, flags, regulatory medical scales, medical items, SKU catalogue, packaging imports, UOMs, consumption and monthly usage — plus role-based access with per-user permission overrides and audit history",
      "Worked on the PO/PM side with the client: ran requirement sessions, turned spreadsheets and loosely described ideas into user stories and acceptance criteria, and mapped business rules (flag-state scales, unit conversions, packaging) into clear specs",
      "Aligned API contracts with the backend team through written backend requests and a shared bug log, and ran QA/UAT feedback rounds with the client before go-live",
      "Set up CI/CD to Azure with GitHub Actions, gated by typecheck, lint and 270+ Vitest/Testing Library test files, plus Husky pre-commit hooks",
    ],
  },
];
